import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Calendar } from "lucide-react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

export type ScheduleAndFAQProps = {
  dates: Array<{ 
    id: string; 
    start: string; 
    end?: string; 
    location?: string; 
    seatsLeft?: number;
    titleKey: string;
    buttonKey: string;
    dateKey: string;
  }>;
  faq: Array<{ 
    categoryKey: string;
    questions: Array<{ qKey: string; aKey: string }>;
  }>;
  defaultOpenFaqIndex?: number;
  onCta?: (courseId: string) => void;
};

export function ScheduleAndFAQ({
  dates,
  faq,
  defaultOpenFaqIndex = 0,
  onCta,
}: ScheduleAndFAQProps) {
  const { t } = useTranslation();

  return (
    <section className="min-h-screen bg-gradient-to-br from-gray-100 to-white flex items-center py-20">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Calendario */}
          <motion.div
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl mb-8 text-orange-500">
              {t("breeders.calendar.title1")}
              <br />
              <span className="text-black">{t("breeders.calendar.title2")}</span>
            </h2>

            <div className="space-y-4">
              {dates.map((date) => (
                <Card key={date.id} className="bg-white border-orange-500/20 p-6 rounded-2xl shadow-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center mb-2">
                        <Calendar className="w-5 h-5 text-orange-500 mr-2" />
                        <span className="text-orange-500">
                          {t(date.dateKey)}
                        </span>
                      </div>
                      <h3 className="text-xl text-black">
                        {t(date.titleKey)}
                      </h3>
                    </div>
                    <Button
                      size="sm"
                      className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-full"
                      onClick={() => onCta?.(date.id)}
                    >
                      {t(date.buttonKey)}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </motion.div>

          {/* FAQs */}
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl mb-8 text-orange-500">
              {t("breeders.faqs.title1")} 
              <br />
              <span className="text-black">{t("breeders.faqs.title2")}</span>
            </h2>

            <div className="space-y-4">
              {/* Categoría 1: Organización y logística del curso */}
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <Accordion type="single" collapsible>
                  <AccordionItem
                    value="categoria-1"
                    className="border-none"
                  >
                    <AccordionTrigger className="px-6 py-4 text-left text-lg text-orange-500 hover:text-orange-600 hover:no-underline">
                      {t("breeders.faqs.categories.0.title")} 
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="space-y-4">
                        <div className="border-b border-gray-100 pb-4">
                          <h4 className="text-orange-500 mb-2">
                            {t("breeders.faqs.categories.0.questions.0.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.0.questions.0.answer")}
                          </p>
                        </div>
                        <div className="border-b border-gray-100 pb-4">
                          <h4 className="text-orange-500 mb-2">
                            {t("breeders.faqs.categories.0.questions.1.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.0.questions.1.answer")}
                          </p>
                        </div>
                        <div className="border-b border-gray-100 pb-4">
                          <h4 className="text-orange-500 mb-2">
                            {t("breeders.faqs.categories.0.questions.2.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.0.questions.2.answer")}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-orange-500 mb-2">
                              {t("breeders.faqs.categories.0.questions.3.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.0.questions.3.answer")}
                          </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {/* Categoría 2: Requisitos y nivel de los alumnos */}
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <Accordion type="single" collapsible>
                  <AccordionItem
                    value="categoria-2"
                    className="border-none"
                  >
                    <AccordionTrigger className="px-6 py-4 text-left text-lg text-orange-500 hover:text-orange-600 hover:no-underline">
                      {t("breeders.faqs.categories.1.title")}
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="space-y-4">
                        <div className="border-b border-gray-100 pb-4">
                          <h4 className="text-orange-500 mb-2">
                                  {t("breeders.faqs.categories.1.questions.0.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.1.questions.0.answer")}
                          </p>
                        </div>
                        <div className="border-b border-gray-100 pb-4">
                          <h4 className="text-orange-500 mb-2">
                            {t("breeders.faqs.categories.1.questions.1.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.1.questions.1.answer")}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-orange-500 mb-2">
                            {t("breeders.faqs.categories.1.questions.2.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.1.questions.2.answer")}
                          </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {/* Categoría 3: Contenido y metodología */}
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <Accordion type="single" collapsible>
                  <AccordionItem
                    value="categoria-3"
                    className="border-none"
                  >
                    <AccordionTrigger className="px-6 py-4 text-left text-lg text-orange-500 hover:text-orange-600 hover:no-underline">
                      {t("breeders.faqs.categories.2.title")}
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="space-y-4">
                        <div className="border-b border-gray-100 pb-4">
                          <h4 className="text-orange-500 mb-2">
                            {t("breeders.faqs.categories.2.questions.0.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.2.questions.0.answer")}
                          </p>
                        </div>
                        <div className="border-b border-gray-100 pb-4">
                          <h4 className="text-orange-500 mb-2">
                            {t("breeders.faqs.categories.2.questions.1.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.2.questions.1.answer")}
                          </p>
                        </div>
                        <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-orange-500 mb-2">
                                {t("breeders.faqs.categories.2.questions.2.question")}
                            </h4>
                            <p className="text-black">
                              {t("breeders.faqs.categories.2.questions.2.answer")}
                            </p>
                        </div>
                        <div className="border-b border-gray-100 pb-4">
                          <h4 className="text-orange-500 mb-2">
                            {t("breeders.faqs.categories.2.questions.3.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.2.questions.3.answer")}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-orange-500 mb-2">
                            {t("breeders.faqs.categories.2.questions.4.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.2.questions.4.answer")}
                          </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {/* Categoría 4: Certificación y soporte */}
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <Accordion type="single" collapsible>
                  <AccordionItem
                    value="categoria-4"
                    className="border-none"
                  >
                    <AccordionTrigger className="px-6 py-4 text-left text-lg text-orange-500 hover:text-orange-600 hover:no-underline">
                      {t("breeders.faqs.categories.3.title")}
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="space-y-4">
                        <div className="border-b border-gray-100 pb-4">
                          <h4 className="text-orange-500 mb-2">
                            {t("breeders.faqs.categories.3.questions.0.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.3.questions.0.answer")}
                          </p>
                        </div>
                        <div className="border-b border-gray-100 pb-4">
                          <h4 className="text-orange-500 mb-2">
                            {t("breeders.faqs.categories.3.questions.1.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.3.questions.1.answer")}
                          </p>
                        </div>
                        <div className="border-b border-gray-100 pb-4">
                          <h4 className="text-orange-500 mb-2">
                            {t("breeders.faqs.categories.3.questions.2.question")}
                          </h4>
                          <p className="text-black">
                            {t("breeders.faqs.categories.3.questions.2.answer")}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-orange-500 mb-2">
                                {t("breeders.faqs.categories.3.questions.3.question")}
                            </h4>
                            <p className="text-black">
                              {t("breeders.faqs.categories.3.questions.3.answer")}
                            </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
